import { CommonModule } from '@angular/common';

import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import { reportsGuard } from 'app/guards/reports.guards';
import { systemGuard } from 'app/guards/system.guards';
import { tripsGuard } from 'app/guards/trips.guards';
import { KuiModule } from 'app/key-ui';

// modules
import { SharedModule } from './shared/shared.module';

import { adminGuard, } from 'app/guards/admin.guards';
import { MatchMediaService } from 'app/services/match-media/match-media.service';
import { assetPerformanceGuard } from './guards/asset-performance.guards';
import { authenticatedGuard } from './guards/auth.guards';
import { dashboardsGuard } from './guards/dashboards.guards';
import { deviceHealthGuard } from './guards/device-health.guards';
import { mapSearchGuard } from './guards/mapsearch.guards';
import { radUiGuard } from './guards/rad-ui.guards';
import { statusGuard } from './guards/status.guards';

import { StaticPageComponent } from './shared/components/static-page/static-page.component';
import { alertsGuard } from './guards/alerts.guards';
import { videoGuard } from './guards/videos.guards';

const publicRoutes: Routes = [
    { 
        path: '', redirectTo: '/status', pathMatch: 'full' 
    },
    {
        path: 'login', 
        loadChildren: () => import('./pages/auth/login/login.module').then(m => m.LoginPageModule),
    },
    {
        path: 'forgot', 
        loadChildren: () => import('./pages/auth/forgot/forgot.module').then(m => m.ForgotPageModule),
    },
    {
        path: 'shared', 
        loadChildren: () => import('./pages/sharing/sharing.module').then(m => m.SharingPageModule),
    },
    {
        path: 'clear', 
        loadChildren: () => import('./pages/auth/clear/clear.module').then(m => m.ClearPageModule),
    },
]

const authenticatedRoutes: Routes = [
    {
        path: 'about',
        loadChildren: () => import('./pages/about/about.module').then(m => m.AboutPageModule),
        canActivate: [authenticatedGuard],
    },
    {
        path: 'user',
        loadChildren: () => import('./pages/user/user.module').then(m => m.UserPageModule),
        canActivate: [authenticatedGuard],
    },
    {
        path: 'otp',
        loadChildren: () => import('./pages/auth/otp/otp.module').then(m => m.OtpPageModule),
        canActivate: [authenticatedGuard]
    },
    {
        path: 'password',
        loadChildren: () => import('./pages/auth/password/password.module').then(m => m.PasswordChangePageModule), canActivate: [authenticatedGuard]
    },
    {
        path: 'welcome',
        loadChildren: () => import('./pages/auth/welcome/welcome.module').then(m => m.WelcomePageModule),
        canActivate: [authenticatedGuard]
    },
    {
        path: 'client-select',
        loadChildren: () => import('./pages/client-select/client-select.module').then(m => m.ClientSelectPageModule), canActivate: [authenticatedGuard]
    },
    {
        path: 'status',
        loadChildren: () => import('./pages/status/overview/overview.module').then(m => m.OverViewPageModule),
        canActivate: [authenticatedGuard, statusGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'videos',
        loadChildren: () => import('./pages/status/videos/videos.routes').then(m => m.VIDEOS_ROUTES),
        canActivate: [authenticatedGuard, videoGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'dashboards',
        loadChildren: () => import('./pages/status/dashboards/dashboards.module').then(m => m.DashboardsPageModule),
        canActivate: [authenticatedGuard, dashboardsGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'alerts',
        loadChildren: () => import('./pages/status/alerts/alerts.module').then(m => m.AlertsPageModule),
        canActivate: [authenticatedGuard, alertsGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'system',
        loadChildren: () => import('./pages/system/explore/explore.module').then(m => m.SystemExplorePageModule),
        canActivate: [authenticatedGuard, systemGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'history',
        loadChildren: () => import('./pages/history/trip-replay/trip-replay.module').then(m => m.TripReplayPageModule),
        canActivate: [authenticatedGuard, tripsGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'mapsearch',
        loadChildren: () => import('./pages/history/mapsearch/mapsearch.module').then(m => m.MapSearchPageModule),
        canActivate: [authenticatedGuard, mapSearchGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'reporting',
        loadChildren: () => import('./pages/history/reporting/reporting.module').then(m => m.ReportingPageModule),
        canActivate: [authenticatedGuard, reportsGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'assetperformance',
        loadChildren: () => import('./pages/history/asset-performance2/asset-performance.routes').then(m => m.ASSET_PERFORMANCE_ROUTES),
        canMatch: [radUiGuard], // if false this falls through to the next assetperformance route
        canActivate: [authenticatedGuard, assetPerformanceGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'assetperformance',
        loadChildren: () => import('./pages/history/asset-performance/asset-performance.routes').then(m => m.ASSET_PERFORMANCE_ROUTES),
        canActivate: [authenticatedGuard, assetPerformanceGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'admin',
        loadChildren: () => import('./pages/admin/explore/explore.module').then(m => m.AdminExplorePageModule),
        canActivate: [authenticatedGuard, adminGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'devicehealth',
        loadChildren: () => import('./pages/admin/devicehealth2/device-health2.routes').then(m => m.DEVICE_HEALTH2_ROUTES),
        canMatch: [radUiGuard], // if false this falls through to the next devicehealth route
        canActivate: [authenticatedGuard, deviceHealthGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'devicehealth',
        loadChildren: () => import('./pages/admin/devicehealth/device-health.routes').then(m => m.DEVICE_HEALTH_ROUTES),
        canActivate: [authenticatedGuard, deviceHealthGuard],
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'demo',
        loadChildren: () => import('./pages/demo/demo.module').then(m => m.KuiDemoModule),
        canActivate: [authenticatedGuard],
    },
    {
        path: 'external', component: StaticPageComponent, canActivate: [authenticatedGuard],
    },
    {
        path: 'external/:tab', component: StaticPageComponent, canActivate: [authenticatedGuard],
    },
    {
        path: '**', component: StaticPageComponent, canActivate: [authenticatedGuard], pathMatch: 'full'
    },
];

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule.forRoot([...publicRoutes, ...authenticatedRoutes], { 
            useHash: true, 
            enableTracing: false, 
            initialNavigation: 'disabled', 
            onSameUrlNavigation: 'reload' // required to re-evaluate route guards on client change
        }),
        KuiModule,
        SharedModule.forRoot(),
        BrowserAnimationsModule,

    ],
    exports: [
        RouterModule,
        KuiModule,
        SharedModule,
    ],
    providers: [
        MatchMediaService,
    ],
})
export class AppRoutingModule { }
